import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Zap, Crown, Sparkles, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageContainer from '../components/PageContainer';
import PageHero from '../components/PageHero';
import PlanComparison from '../components/PlanComparison';
import SEO from '../components/SEO';
import usePageTitle from '../hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  usePageTitle('Preise');
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [allowedUpgrades, setAllowedUpgrades] = useState([]);
  const [allowedPlanChanges, setAllowedPlanChanges] = useState([]);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralBenefit, setReferralBenefit] = useState(null);
  const [earlyAdopterStatus, setEarlyAdopterStatus] = useState(null);

  useEffect(() => {
    loadPlans();
    if (user && user.role === 'provider') {
      loadCurrentSubscription();
      loadEarlyAdopterStatus();
    }
  }, [user]);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEarlyAdopterStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/early-adopter-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarlyAdopterStatus(response.data);
    } catch (error) {
      console.error('Error loading early adopter status:', error);
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.has_subscription) {
        setCurrentPlan(response.data.current_plan_id);
        setAllowedUpgrades(response.data.allowed_upgrades || []);
        setAllowedPlanChanges(response.data.allowed_plan_changes || []);
        setCancelAtPeriodEnd(response.data.cancel_at_period_end || false);
        setPendingPlanChange(response.data.pending_plan_change || null);
      } else {
        // No subscription - all plans are available
        setAllowedPlanChanges(response.data.allowed_plan_changes || []);
      }
    } catch (error) {
      console.error('Error loading current subscription:', error);
    }
  };

  const validateReferralCode = async (code) => {
    if (!code || code.trim() === '') {
      setReferralValid(null);
      setReferralBenefit(null);
      return;
    }

    setReferralValidating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/provider/referral/validate`, {
        code: code.trim()
      }, { headers });
      
      setReferralValid(true);
      setReferralBenefit(response.data.benefit);
    } catch (error) {
      setReferralValid(false);
      setReferralBenefit(null);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      }
    } finally {
      setReferralValidating(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (user.role !== 'provider') {
      alert('Nur Dienstleister können ein Abo abschließen.');
      return;
    }

    setCheckingOut(planId);

    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;

      const checkoutData = {
        plan_id: planId,
        origin_url: originUrl
      };

      // Add referral code if valid
      if (referralCode && referralValid) {
        checkoutData.referral_code = referralCode.trim();
      }

      const response = await axios.post(
        `${API}/subscription/checkout`,
        checkoutData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      // Check if we need to use upgrade or downgrade endpoint
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.code === 'USE_UPGRADE_ENDPOINT') {
        await handleUpgrade(planId);
      } else if (errorDetail?.code === 'USE_DOWNGRADE_ENDPOINT') {
        await handleDowngrade(planId);
      } else {
        // Show actual error for debugging
        const errorMessage = typeof errorDetail === 'string' 
          ? errorDetail 
          : errorDetail?.message || error.message || 'Unbekannter Fehler';
        alert(`Fehler: ${errorMessage}`);
        setCheckingOut(null);
      }
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/subscription/upgrade`,
        { plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        // Navigate to subscription management to see the new plan
        navigate('/provider/dashboard?tab=subscription');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Upgrade. Bitte versuche es später erneut.';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setCheckingOut(null);
    }
  };

  const handleDowngrade = async (planId) => {
    // Confirm downgrade with user
    const confirmDowngrade = window.confirm(
      'Möchtest du wirklich zu einem niedrigeren Plan wechseln? Der Wechsel erfolgt sofort. Die nächste Abrechnung erfolgt zum neuen Preis.'
    );

    if (!confirmDowngrade) {
      return;
    }

    setCheckingOut(planId);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/subscription/downgrade`,
        { plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        // Navigate to subscription management to see the new plan
        navigate('/provider/dashboard?tab=subscription');
      }
    } catch (error) {
      console.error('Downgrade error:', error);
      // Show the actual error message from the backend
      const errorMessage = error.response?.data?.detail || 'Fehler beim Downgrade. Bitte versuche es später erneut.';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setCheckingOut(null);
    }
  };

  const getPlanIcon = (planId) => {
    const icons = {
      basic_monthly: Zap,
      basic_yearly: Zap,
      plus: Sparkles,
      pro: Crown
    };
    return icons[planId] || Zap;
  };

  const getPlanColor = (planId) => {
    const colors = {
      basic_monthly: 'from-[#2B2142] to-[#3D3158]',
      basic_yearly: 'from-[#2B2142] to-[#3D3158]',
      plus: 'from-purple-500 to-purple-600',
      pro: 'from-amber-500 to-amber-600'
    };
    return colors[planId] || 'from-[#2B2142] to-[#3D3158]';
  };

  const isPro = (planId) => planId === 'pro';
  const isYearly = (planId) => planId === 'basic_yearly';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2]">
      <SEO
        title="Preise & Abos für Dienstleister"
        description="Transparente Preise für Event-Dienstleister. Ab 16,99€/Monat mit unbegrenzten Anfragen und 0% Provision. Monatlich kündbar."
        keywords="EventConnect Preise, Dienstleister Abo, Event Services Kosten"
        url="/pricing"
      />
      <Navbar user={user} onLogout={onLogout} />

      {/* Hero Section */}
      <PageHero
        title="Wähle deinen EventConnect-Plan"
        subtitle="Keine Provision auf Aufträge. Klare, monatliche oder jährliche Gebühren. Endkunden nutzen die Plattform kostenlos."
      />
      
      <div className="pb-16">
        <PageContainer size="large">
          {/* Benefits Banner */}
          <div className="bg-gradient-to-r from-[#2B2142] to-[#3D3158] rounded-3xl p-8 mb-12 text-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold mb-2">0%</div>
                <div className="text-sm opacity-90">Keine Provision auf Aufträge</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">30 Tage</div>
                <div className="text-sm opacity-90">Kostenlos testen (Basic Monthly)</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">Flexibel</div>
                <div className="text-sm opacity-90">Kündbar zum Periodenende</div>
              </div>
            </div>
          </div>

          {/* EARLY6 Promo Banner - Above Plans */}
          {earlyAdopterStatus && earlyAdopterStatus.available && (
            <div className="mb-8">
              <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl shadow-2xl p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  {/* Left: Text Content */}
                  <div className="flex-1 text-white">
                    <div className="flex items-center gap-3 mb-3">
                      <Sparkles className="w-6 h-6 md:w-8 md:h-8" />
                      <h2 className="text-2xl md:text-3xl font-bold">
                        Early-Adopter-Angebot für Dienstleister
                      </h2>
                    </div>
                    <p className="text-lg md:text-xl mb-2 font-medium">
                      Die ersten 100 Dienstleister erhalten die ersten <strong>6 Monate Basic-Abo gratis</strong> – mit dem Code <strong>EARLY6</strong>
                    </p>
                    <p className="text-sm md:text-base opacity-90">
                      Gültig nur für das Basic-Abo monatlich. Ab dem 7. Monat nur 16,99 € netto/Monat zzgl. MwSt.
                    </p>
                    
                    {/* Remaining Slots Indicator */}
                    <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-semibold">
                        Noch {earlyAdopterStatus.remaining_slots} von 100 Plätzen verfügbar
                      </span>
                    </div>
                  </div>
                  
                  {/* Right: CTA */}
                  <div className="w-full md:w-auto">
                    <button
                      onClick={() => {
                        // Scroll to Basic Monthly plan
                        const basicPlan = document.getElementById('basic-monthly-plan');
                        if (basicPlan) {
                          basicPlan.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          basicPlan.classList.add('ring-4', 'ring-emerald-400');
                          setTimeout(() => {
                            basicPlan.classList.remove('ring-4', 'ring-emerald-400');
                          }, 2000);
                        }
                      }}
                      className="w-full md:w-auto bg-white hover:bg-gray-100 text-emerald-600 font-bold py-4 px-8 rounded-xl transition-all transform hover:scale-105 shadow-lg text-lg whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <Zap className="w-5 h-5" />
                      Jetzt Early-Adopter werden
                    </button>
                    <p className="text-xs text-white/80 text-center mt-2">
                      EARLY6 wird automatisch angewendet
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Referral Code Section */}
          <div className="max-w-md mx-auto mb-8 p-6 bg-[#FFF0D1]/50 border border-[#2B2142]/20 rounded-2xl">
            <h3 className="font-bold text-gray-900 mb-2">Hast du einen Empfehlungscode?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Gib hier deinen Code ein, um 60 Tage kostenlos zu testen (statt 30 Tage).
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="z.B. EC-COMPANY-XXXX oder FRIENDS"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-[#2B2142]"
              />
              <button
                onClick={() => validateReferralCode(referralCode)}
                disabled={!referralCode || referralValidating}
                className="px-4 py-2 bg-[#2B2142] text-white rounded-lg hover:bg-[#1E1730] disabled:opacity-50"
              >
                {referralValidating ? '...' : 'Prüfen'}
              </button>
            </div>
            {referralValid === true && (
              <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded-lg text-green-800 text-sm">
                ✓ Code gültig! {referralBenefit}
              </div>
            )}
            {referralValid === false && (
              <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-lg text-red-800 text-sm">
                ✗ Ungültiger Code
              </div>
            )}
          </div>

          {/* Pricing Cards */}
          <div id="plans" className="scroll-mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(plans)
              .filter(([planId]) => planId !== 'early_adopter_basic') // Exclude Early Adopter from standard grid
              .map(([planId, plan]) => {
              const Icon = getPlanIcon(planId);
              const isProPlan = isPro(planId);
              const isYearlyPlan = isYearly(planId);
              const isBasicMonthly = planId === 'basic_monthly';
              
              // Display netto price (zzgl. 19% MwSt)
              const nettoPrice = plan.price;

              return (
                <div
                  key={planId}
                  id={planId === 'basic_monthly' ? 'basic-monthly-plan' : undefined}
                  className={`relative bg-white rounded-3xl shadow-xl p-8 flex flex-col transition-transform hover:scale-105 ${
                    isProPlan ? 'ring-4 ring-amber-400' : ''
                  }`}
                  style={{ minHeight: '600px' }}
                >
                  {/* Badge for special plans - RIGHT TOP CORNER */}
                  {isProPlan && (
                    <div className="absolute -top-3 -right-3">
                      <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-lg">
                        BELIEBT
                      </span>
                    </div>
                  )}
                  
                  {isYearlyPlan && (
                    <div className="absolute -top-3 -right-3">
                      <span className="bg-green-600 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-lg">
                        SPARE 2 MONATE
                      </span>
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getPlanColor(planId)} flex items-center justify-center mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>

                  {/* Price - NETTO (ALL IN ONE LINE) */}
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-gray-900">
                      {nettoPrice.toFixed(2).replace('.', ',')} € / {plan.interval === 'month' ? 'Monat' : 'Jahr'}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">zzgl. 19% MwSt.</p>
                    
                    {/* Laufzeit-Text */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {isYearlyPlan 
                          ? 'Jahresbindung, kündbar zum Ende des Jahres' 
                          : 'Monatliche Abrechnung, kündbar zum Monatsende'}
                      </p>
                    </div>
                    
                    {/* Trial Badge */}
                    {plan.trial_days > 0 && isBasicMonthly && (
                      <div className="mt-3 px-3 py-2 bg-green-50 border-2 border-green-200 rounded-lg">
                        <p className="text-sm font-semibold text-green-800">
                          🎉 30 Tage kostenlos testen
                        </p>
                      </div>
                    )}
                    
                    {/* Yearly savings hint */}
                    {isYearlyPlan && (
                      <p className="mt-2 text-xs text-green-700 font-medium">
                        Spare 2 Monate im Vergleich zum Monatsabo
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  {(() => {
                    const isCurrentPlan = currentPlan === planId;
                    const isUpgradeAllowed = allowedUpgrades.includes(planId);
                    const isPlanChangeAllowed = allowedPlanChanges.includes(planId);
                    const isDowngrade = currentPlan && !isUpgradeAllowed && !isCurrentPlan && isPlanChangeAllowed;
                    const isPendingDowngradeToPlan = pendingPlanChange === planId;

                    // Current plan button
                    if (isCurrentPlan && !cancelAtPeriodEnd) {
                      return (
                        <button
                          disabled
                          className="w-full py-4 rounded-xl font-semibold bg-gray-400 text-white cursor-not-allowed"
                        >
                          Aktiver Plan
                        </button>
                      );
                    }

                    // Current plan but cancelled - show as "Gekündigt"
                    if (isCurrentPlan && cancelAtPeriodEnd) {
                      return (
                        <button
                          disabled
                          className="w-full py-4 rounded-xl font-semibold bg-orange-400 text-white cursor-not-allowed"
                        >
                          Aktueller Plan (gekündigt)
                        </button>
                      );
                    }

                    // Pending downgrade to this plan
                    if (isPendingDowngradeToPlan) {
                      return (
                        <button
                          disabled
                          className="w-full py-4 rounded-xl font-semibold bg-[#3D3158] text-white cursor-not-allowed"
                        >
                          Wechsel geplant
                        </button>
                      );
                    }

                    // Downgrade option (only for active subscriptions without pending cancellation)
                    if (isDowngrade && !cancelAtPeriodEnd) {
                      return (
                        <button
                          onClick={() => handleDowngrade(planId)}
                          disabled={checkingOut === planId}
                          className="w-full py-4 rounded-xl font-semibold transition-all bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-50"
                        >
                          {checkingOut === planId
                            ? 'Wird geladen...'
                            : `Downgrade auf ${plan.name}`
                          }
                        </button>
                      );
                    }

                    // Standard select/upgrade button
                    return (
                      <button
                        onClick={() => handleSelectPlan(planId)}
                        disabled={checkingOut === planId}
                        className={`w-full py-4 rounded-xl font-semibold transition-all ${
                          isProPlan
                            ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg'
                            : 'bg-[#2B2142] hover:bg-[#1E1730] text-white'
                        } disabled:opacity-50`}
                      >
                        {checkingOut === planId
                          ? 'Wird geladen...'
                          : (currentPlan && isUpgradeAllowed ? `Upgrade auf ${plan.name}` : 'Jetzt wählen')
                        }
                      </button>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </PageContainer>
      </div>

      {/* Detailed Plan Comparison Table */}
      <PlanComparison />

      {/* Additional Info */}
      <div className="bg-white pb-16 md:pb-24">
        <PageContainer>
          <div className="mt-16 mb-16 md:mb-24 text-center">
            <div className="bg-white rounded-3xl shadow-xl p-8 max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Warum EventConnect?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Für Dienstleister</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Keine Provision auf Aufträge</li>
                    <li>• Fixe monatliche oder jährliche Kosten</li>
                    <li>• Kündbar zum Ende der Abrechnungsperiode</li>
                    <li>• Direkter Kundenkontakt</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Für Kunden</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Kostenlose Nutzung</li>
                    <li>• Keine versteckten Gebühren</li>
                    <li>• Direkte Anfragen an Dienstleister</li>
                    <li>• Transparente Bewertungen</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </PageContainer>
      </div>

      <Footer />
    </div>
  );
};

export default PricingPage;
